import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const RateAdjustmentModal = ({ isOpen, onClose, employeeData, currentRates, onSubmitSuccess, existingRequest }) => {
  const [formData, setFormData] = useState({
    company: '',
    reason: '',
    justification: '',
    achievements: '',
    requested_effective_date: '',
    requested_check_rate: '',
    requested_weekday_cash_rate: '',
    requested_weekend_cash_rate: ''
  });
  
  const [selectedRate, setSelectedRate] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isOpen && currentRates && currentRates.length > 0) {
      if (existingRequest) {
        // Pre-fill form with existing rejected request data
        const companyRate = currentRates.find(r => r.company === existingRequest.company) || currentRates[0];
        setSelectedRate(companyRate);
        setFormData({
          company: existingRequest.company,
          reason: existingRequest.reason || '',
          justification: existingRequest.justification || '',
          achievements: existingRequest.achievements || '',
          requested_effective_date: existingRequest.requested_effective_date ? existingRequest.requested_effective_date.split('T')[0] : '',
          requested_check_rate: (parseFloat(existingRequest.requested_check_rate) || 0).toString(),
          requested_weekday_cash_rate: (parseFloat(existingRequest.requested_weekday_cash_rate) || 0).toString(),
          requested_weekend_cash_rate: (parseFloat(existingRequest.requested_weekend_cash_rate) || 0).toString()
        });
      } else {
        // Default to first company if not selected
        if (!selectedRate) {
          selectCompanyRate(currentRates[0]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentRates, existingRequest]);

  const selectCompanyRate = (rate) => {
    setSelectedRate(rate);
    setFormData({
      ...formData,
      company: rate.company,
      requested_check_rate: (parseFloat(rate.check_rate) || 0).toString(),
      requested_weekday_cash_rate: (parseFloat(rate.weekday_cash_rate) || 0).toString(),
      requested_weekend_cash_rate: (parseFloat(rate.weekend_cash_rate) || 0).toString()
    });
  };

  // Helper function to check if a rate exists (not 0 or null)
  const hasRate = (rateValue) => {
    return parseFloat(rateValue) > 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const calculateIncrease = (current, requested) => {
    const currentVal = parseFloat(current) || 0;
    const requestedVal = parseFloat(requested) || 0;
    const increase = requestedVal - currentVal;
    const percentage = currentVal > 0 ? ((increase / currentVal) * 100) : 0;
    
    return {
      amount: increase,
      percentage: percentage,
      formatted: increase >= 0 ? `+$${increase.toFixed(2)}` : `-$${Math.abs(increase).toFixed(2)}`,
      percentFormatted: `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`
    };
  };

  const calculateAnnualImpact = () => {
    if (!selectedRate) return 0;
    
    // Estimate based on typical hours per year
    // Assuming: 40 hours/week check, 20 hours/week weekday cash, 10 hours/week weekend cash
    const checkIncrease = calculateIncrease(selectedRate.check_rate, formData.requested_check_rate);
    const weekdayIncrease = calculateIncrease(selectedRate.weekday_cash_rate, formData.requested_weekday_cash_rate);
    const weekendIncrease = calculateIncrease(selectedRate.weekend_cash_rate, formData.requested_weekend_cash_rate);
    
    const annualImpact = 
      (checkIncrease.amount * 40 * 52) +
      (weekdayIncrease.amount * 20 * 52) +
      (weekendIncrease.amount * 10 * 52);
    
    return annualImpact;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.company) {
      newErrors.company = 'Please select a company';
    }
    
    if (!formData.reason) {
      newErrors.reason = 'Please select a reason';
    }
    
    if (!formData.justification || formData.justification.trim().length < 20) {
      newErrors.justification = 'Justification must be at least 20 characters';
    }
    
    if (!formData.requested_effective_date) {
      newErrors.requested_effective_date = 'Please select an effective date';
    }
    
    // Validate that at least one rate is increased (only for rates that exist)
    const checkIncrease = hasRate(selectedRate?.check_rate) ? calculateIncrease(selectedRate.check_rate, formData.requested_check_rate) : { amount: 0 };
    const weekdayIncrease = hasRate(selectedRate?.weekday_cash_rate) ? calculateIncrease(selectedRate.weekday_cash_rate, formData.requested_weekday_cash_rate) : { amount: 0 };
    const weekendIncrease = hasRate(selectedRate?.weekend_cash_rate) ? calculateIncrease(selectedRate.weekend_cash_rate, formData.requested_weekend_cash_rate) : { amount: 0 };
    
    // Check if any existing rate is being decreased
    if ((hasRate(selectedRate?.check_rate) && checkIncrease.amount < 0) ||
        (hasRate(selectedRate?.weekday_cash_rate) && weekdayIncrease.amount < 0) ||
        (hasRate(selectedRate?.weekend_cash_rate) && weekendIncrease.amount < 0)) {
      newErrors.rates = 'Requested rates cannot be lower than current rates';
    }
    
    // Check if at least one rate is being increased
    if (checkIncrease.amount === 0 && weekdayIncrease.amount === 0 && weekendIncrease.amount === 0) {
      newErrors.rates = 'At least one rate must be increased';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setShowPreview(true);
  };

  const confirmSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      const requestData = {
        employee_number: employeeData.employee_number,  // Primary key for linking
        employee_name: employeeData.payroll_name || null,  // Display name, nullable
        company: formData.company,
        department: employeeData.department || null,
        
        // Current rates (only include rates that exist)
        current_check_rate: hasRate(selectedRate.check_rate) ? parseFloat(selectedRate.check_rate) : 0,
        current_weekday_cash_rate: hasRate(selectedRate.weekday_cash_rate) ? parseFloat(selectedRate.weekday_cash_rate) : 0,
        current_weekend_cash_rate: hasRate(selectedRate.weekend_cash_rate) ? parseFloat(selectedRate.weekend_cash_rate) : 0,
        
        // Requested rates (only include rates that exist)
        requested_check_rate: hasRate(selectedRate.check_rate) ? parseFloat(formData.requested_check_rate) : 0,
        requested_weekday_cash_rate: hasRate(selectedRate.weekday_cash_rate) ? parseFloat(formData.requested_weekday_cash_rate) : 0,
        requested_weekend_cash_rate: hasRate(selectedRate.weekend_cash_rate) ? parseFloat(formData.requested_weekend_cash_rate) : 0,
        
        // Details
        reason: formData.reason,
        justification: formData.justification.trim(),
        achievements: formData.achievements.trim() || null,
        requested_effective_date: formData.requested_effective_date,
        
        status: 'submitted'
      };
      
      const { data, error } = await supabase
        .from('pay_rate_adjustment_requests')
        .insert([requestData])
        .select();
      
      if (error) {
        console.error('Error submitting request:', error);
        alert('Error submitting request: ' + error.message);
        return;
      }
      
      // Update whitelist counter
      await supabase.rpc('increment_rate_request_counter', { 
        emp_number: employeeData.employee_number 
      });
      
      // Send SMS notification to admin
      try {
        const adminPhoneNumber = '+19299906651';
        const employeeName = employeeData.payroll_name || employeeData.employee_number;
        const requestNo = data[0].request_no;
        
        // Build rate summary
        let rateSummary = '';
        if (requestData.requested_check_rate > 0) {
          rateSummary += `Check: $${requestData.requested_check_rate.toFixed(2)}/hr`;
        }
        if (requestData.requested_weekday_cash_rate > 0) {
          if (rateSummary) rateSummary += ', ';
          rateSummary += `Weekday Cash: $${requestData.requested_weekday_cash_rate.toFixed(2)}/hr`;
        }
        if (requestData.requested_weekend_cash_rate > 0) {
          if (rateSummary) rateSummary += ', ';
          rateSummary += `Weekend Cash: $${requestData.requested_weekend_cash_rate.toFixed(2)}/hr`;
        }
        
        const adminMessage = `🚨 NEW RATE ADJUSTMENT REQUEST\n\nRequest #${requestNo}\nEmployee: ${employeeName} (${employeeData.employee_number})\nCompany: ${requestData.company}\n\nRequested Rates:\n${rateSummary}\n\nPlease review at https://pay.starside.app`;
        
        const smsResponse = await fetch('https://paystub.roborx.workers.dev/api/send-sms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            recipients: [{
              employee_name: 'Admin',
              employee_number: 'admin',
              phone: adminPhoneNumber,
              message: adminMessage
            }],
            sendAll: false,
            skipPayrollValidation: true // Skip validation for admin notifications
          })
        });
        
        if (smsResponse.ok) {
          console.log('✅ Admin SMS notification sent successfully');
        } else {
          console.warn('⚠️ Failed to send admin SMS notification:', await smsResponse.text());
        }
      } catch (smsError) {
        // Don't fail the request submission if SMS fails
        console.error('Error sending admin SMS notification:', smsError);
      }
      
      const successMessage = existingRequest
        ? `✅ Your edited rate adjustment request has been resubmitted!\n\nNew Request Number: ${data[0].request_no}\n\nPrevious Request: ${existingRequest.request_no}\n\nYou will be notified when it is reviewed.`
        : `✅ Your rate adjustment request has been submitted!\n\nRequest Number: ${data[0].request_no}\n\nYou will be notified when it is reviewed.`;
      
      alert(successMessage);
      
      if (onSubmitSuccess) {
        onSubmitSuccess(data[0]);
      }
      
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error in confirmSubmit:', error);
      alert('Error submitting request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      company: '',
      reason: '',
      justification: '',
      achievements: '',
      requested_effective_date: '',
      requested_check_rate: '',
      requested_weekday_cash_rate: '',
      requested_weekend_cash_rate: ''
    });
    setSelectedRate(null);
    setErrors({});
    setShowPreview(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const checkIncrease = selectedRate ? calculateIncrease(selectedRate.check_rate, formData.requested_check_rate) : null;
  const weekdayIncrease = selectedRate ? calculateIncrease(selectedRate.weekday_cash_rate, formData.requested_weekday_cash_rate) : null;
  const weekendIncrease = selectedRate ? calculateIncrease(selectedRate.weekend_cash_rate, formData.requested_weekend_cash_rate) : null;
  const annualImpact = calculateAnnualImpact();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className={`bg-gradient-to-r ${existingRequest ? 'from-blue-600 to-blue-700' : 'from-green-600 to-green-700'} text-white p-6 rounded-t-lg`}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">
                {existingRequest ? 'Edit & Resubmit Rate Request' : 'Request Pay Rate Adjustment'}
              </h2>
              <p className="text-green-100 text-sm mt-1">
                {existingRequest 
                  ? `Editing rejected request ${existingRequest.request_no} - Make changes and resubmit`
                  : 'Submit a formal request for a pay rate increase'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {!showPreview ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Selection */}
              {currentRates && currentRates.length > 1 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Select Company</label>
                  <div className="flex gap-3">
                    {currentRates.map((rate) => (
                      <button
                        key={rate.id}
                        type="button"
                        onClick={() => selectCompanyRate(rate)}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                          selectedRate?.id === rate.id
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {rate.company}
                      </button>
                    ))}
                  </div>
                  {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
                </div>
              )}

              {selectedRate && (
                <>
                  {/* Current Rates Display */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-3">Current Rates ({selectedRate.company})</h3>
                    <div className={`grid grid-cols-1 gap-4 ${
                      [hasRate(selectedRate.check_rate), hasRate(selectedRate.weekday_cash_rate), hasRate(selectedRate.weekend_cash_rate)].filter(Boolean).length === 3 
                        ? 'md:grid-cols-3' 
                        : [hasRate(selectedRate.check_rate), hasRate(selectedRate.weekday_cash_rate), hasRate(selectedRate.weekend_cash_rate)].filter(Boolean).length === 2
                        ? 'md:grid-cols-2'
                        : 'md:grid-cols-1'
                    }`}>
                      {hasRate(selectedRate.check_rate) && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Check Rate</p>
                          <p className="text-xl font-bold text-gray-800">${parseFloat(selectedRate.check_rate).toFixed(2)}/hr</p>
                        </div>
                      )}
                      {hasRate(selectedRate.weekday_cash_rate) && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Weekday Cash</p>
                          <p className="text-xl font-bold text-gray-800">${parseFloat(selectedRate.weekday_cash_rate).toFixed(2)}/hr</p>
                        </div>
                      )}
                      {hasRate(selectedRate.weekend_cash_rate) && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Weekend Cash</p>
                          <p className="text-xl font-bold text-gray-800">${parseFloat(selectedRate.weekend_cash_rate).toFixed(2)}/hr</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Requested Rates */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-3">Requested New Rates</h3>
                    <div className={`grid grid-cols-1 gap-4 ${
                      [hasRate(selectedRate.check_rate), hasRate(selectedRate.weekday_cash_rate), hasRate(selectedRate.weekend_cash_rate)].filter(Boolean).length === 3 
                        ? 'md:grid-cols-3' 
                        : [hasRate(selectedRate.check_rate), hasRate(selectedRate.weekday_cash_rate), hasRate(selectedRate.weekend_cash_rate)].filter(Boolean).length === 2
                        ? 'md:grid-cols-2'
                        : 'md:grid-cols-1'
                    }`}>
                      {/* Check Rate */}
                      {hasRate(selectedRate.check_rate) && (
                        <div>
                          <label className="block text-xs text-gray-600 uppercase mb-1">Check Rate ($/hr)</label>
                          <input
                            type="number"
                            name="requested_check_rate"
                            value={formData.requested_check_rate}
                            onChange={handleInputChange}
                            step="0.01"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                          {checkIncrease && checkIncrease.amount !== 0 && (
                            <p className={`text-xs mt-1 font-semibold ${checkIncrease.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {checkIncrease.formatted} ({checkIncrease.percentFormatted})
                            </p>
                          )}
                        </div>
                      )}

                      {/* Weekday Cash Rate */}
                      {hasRate(selectedRate.weekday_cash_rate) && (
                        <div>
                          <label className="block text-xs text-gray-600 uppercase mb-1">Weekday Cash ($/hr)</label>
                          <input
                            type="number"
                            name="requested_weekday_cash_rate"
                            value={formData.requested_weekday_cash_rate}
                            onChange={handleInputChange}
                            step="0.01"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                          {weekdayIncrease && weekdayIncrease.amount !== 0 && (
                            <p className={`text-xs mt-1 font-semibold ${weekdayIncrease.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {weekdayIncrease.formatted} ({weekdayIncrease.percentFormatted})
                            </p>
                          )}
                        </div>
                      )}

                      {/* Weekend Cash Rate */}
                      {hasRate(selectedRate.weekend_cash_rate) && (
                        <div>
                          <label className="block text-xs text-gray-600 uppercase mb-1">Weekend Cash ($/hr)</label>
                          <input
                            type="number"
                            name="requested_weekend_cash_rate"
                            value={formData.requested_weekend_cash_rate}
                            onChange={handleInputChange}
                            step="0.01"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                          {weekendIncrease && weekendIncrease.amount !== 0 && (
                            <p className={`text-xs mt-1 font-semibold ${weekendIncrease.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {weekendIncrease.formatted} ({weekendIncrease.percentFormatted})
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    {errors.rates && <p className="text-red-500 text-sm mt-2">{errors.rates}</p>}
                  </div>

                  {/* Estimated Annual Impact */}
                  {annualImpact > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Estimated Annual Impact</p>
                      <p className="text-2xl font-bold text-gray-800">
                        +${Math.abs(annualImpact).toFixed(2)}/year
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Based on estimated hours per rate type</p>
                    </div>
                  )}

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Reason for Request *</label>
                    <select
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a reason...</option>
                      <option value="Annual Review">Annual Review</option>
                      <option value="Increased Responsibilities">Increased Responsibilities</option>
                      <option value="Market Rate Adjustment">Market Rate Adjustment</option>
                      <option value="Performance-Based">Performance-Based</option>
                      <option value="Promotion">Promotion</option>
                      <option value="Cost of Living">Cost of Living</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.reason && <p className="text-red-500 text-sm mt-1">{errors.reason}</p>}
                  </div>

                  {/* Justification */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      理由说明 / Justification * <span className="text-xs text-gray-500 font-normal">(请用中文，至少20字 / Please use Chinese, minimum 20 characters)</span>
                    </label>
                    <textarea
                      name="justification"
                      value={formData.justification}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="请用中文说明为什么您应该获得加薪。包括您的贡献、职责和任何相关情况... (Please explain in Chinese why you deserve this rate increase. Include your contributions, responsibilities, and any relevant context...)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                    <div className="flex justify-between items-center mt-1">
                      {errors.justification && <p className="text-red-500 text-sm">{errors.justification}</p>}
                      <p className="text-xs text-gray-500 ml-auto">{formData.justification.length} 字符 / characters</p>
                    </div>
                  </div>

                  {/* Achievements (Optional) */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      工作成就 / Achievements <span className="text-xs text-gray-500 font-normal">(可选，请用中文 / optional, please use Chinese)</span>
                    </label>
                    <textarea
                      name="achievements"
                      value={formData.achievements}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="列出您的主要成就、证书或贡献... (List your key accomplishments, certifications, or contributions...)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  {/* Requested Effective Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Requested Effective Date *</label>
                    <input
                      type="date"
                      name="requested_effective_date"
                      value={formData.requested_effective_date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                    {errors.requested_effective_date && <p className="text-red-500 text-sm mt-1">{errors.requested_effective_date}</p>}
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors"
                >
                  Review & Submit
                </button>
              </div>
            </form>
          ) : (
            /* Preview Screen */
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Request Summary</h3>
                
                {/* Rate Comparison Table */}
                <div className="bg-white rounded-lg overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left">Rate Type</th>
                        <th className="px-4 py-2 text-right">Current</th>
                        <th className="px-4 py-2 text-center">→</th>
                        <th className="px-4 py-2 text-right">Requested</th>
                        <th className="px-4 py-2 text-right">Increase</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hasRate(selectedRate.check_rate) && (
                        <tr className="border-t">
                          <td className="px-4 py-2 font-semibold">Check Rate</td>
                          <td className="px-4 py-2 text-right">${parseFloat(selectedRate.check_rate).toFixed(2)}</td>
                          <td className="px-4 py-2 text-center">→</td>
                          <td className="px-4 py-2 text-right font-semibold">${parseFloat(formData.requested_check_rate).toFixed(2)}</td>
                          <td className="px-4 py-2 text-right text-green-600 font-semibold">
                            {checkIncrease.formatted} ({checkIncrease.percentFormatted})
                          </td>
                        </tr>
                      )}
                      {hasRate(selectedRate.weekday_cash_rate) && (
                        <tr className="border-t">
                          <td className="px-4 py-2 font-semibold">Weekday Cash</td>
                          <td className="px-4 py-2 text-right">${parseFloat(selectedRate.weekday_cash_rate).toFixed(2)}</td>
                          <td className="px-4 py-2 text-center">→</td>
                          <td className="px-4 py-2 text-right font-semibold">${parseFloat(formData.requested_weekday_cash_rate).toFixed(2)}</td>
                          <td className="px-4 py-2 text-right text-green-600 font-semibold">
                            {weekdayIncrease.formatted} ({weekdayIncrease.percentFormatted})
                          </td>
                        </tr>
                      )}
                      {hasRate(selectedRate.weekend_cash_rate) && (
                        <tr className="border-t">
                          <td className="px-4 py-2 font-semibold">Weekend Cash</td>
                          <td className="px-4 py-2 text-right">${parseFloat(selectedRate.weekend_cash_rate).toFixed(2)}</td>
                          <td className="px-4 py-2 text-center">→</td>
                          <td className="px-4 py-2 text-right font-semibold">${parseFloat(formData.requested_weekend_cash_rate).toFixed(2)}</td>
                          <td className="px-4 py-2 text-right text-green-600 font-semibold">
                            {weekendIncrease.formatted} ({weekendIncrease.percentFormatted})
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Company:</p>
                    <p className="font-semibold">{formData.company}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Effective Date:</p>
                    <p className="font-semibold">{new Date(formData.requested_effective_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Reason:</p>
                    <p className="font-semibold">{formData.reason}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Est. Annual Impact:</p>
                    <p className="font-semibold text-green-600">+${annualImpact.toFixed(2)}</p>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-gray-600 text-sm mb-1">Justification:</p>
                  <p className="text-sm bg-white p-3 rounded">{formData.justification}</p>
                </div>

                {formData.achievements && (
                  <div className="mt-3">
                    <p className="text-gray-600 text-sm mb-1">Achievements:</p>
                    <p className="text-sm bg-white p-3 rounded whitespace-pre-wrap">{formData.achievements}</p>
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  ⚠️ <strong>Important:</strong> Once submitted, this request will be reviewed by management. 
                  You will be notified via SMS when a decision is made.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPreview(false)}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors disabled:opacity-50"
                >
                  ← Back to Edit
                </button>
                <button
                  onClick={confirmSubmit}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    '✅ Confirm & Submit'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateAdjustmentModal;

